import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "./components/Header";
import Admin from "./components/Admin";
import Report from "./components/Report";
import AIAgent from "./components/AIAgent";
import Pools from "./components/Pools";
import PoolsCompare from "./components/PoolsCompare";
import GradeLegend from "./components/GradeLegend";
import { fetchDefiProtocols, fetchProtocolScore, fetchTokens, fetchOptions, fetchCategoryOptions, fetchBlockchainOptions, fetchTokenOptions } from "./services/defiService";
import { trackReportView, trackSearch, trackFilter, trackProtocolView } from "./utils/analytics";

// English text constants
const t = {
  loading: "Loading DeFi projects...",
  loadError: "Failed to load DeFi projects",
  loadMoreError: "Failed to load more projects",
  loadMoreButton: "Load More",
  loadingMoreText: "Loading more projects...",
  loadingShort: "Loading...",
  chainLabel: "Chain",
  tvlLabel: "TVL",
  scoreLabel: "Credit Score",
  categoryLabel: "Category",
  filterByCategory: "Filter by Category",
  filterByChain: "Filter by Chain",
  allCategories: "All Categories",
  allChains: "All Chains",
  sortBy: "Sort by",
  // sortByComposite: "Composite", // 已去掉
  sortByTvl: "TVL",
  sortByScore: "Credit Score",
  summaryLabel: "Summary",
  noDescription: "No description yet.",
  readMore: "Read more",
  collapse: "Collapse",
  website: "Website",
  viewReport: "View Report",
  launchApp: "Launch App",
  homepageUnavailable: "No link available",
  paginationStatus: (shown, total) => `Showing ${shown} of ${total} projects`,
  modalTitle: "Credit Report",
  modalClose: "Close report",
  reportLoading: "Loading report...",
  reportError: "Unable to fetch report. Please try again later.",
  unknown: "Unknown",
  coreRisks: "Top Risk Drivers",
  actionableAdvice: "Actionable Advice",
  disclaimers: "Disclaimers",
  openReport: (name) => `${name} credit report`,
  noScoreText: "TVL too low, no score",
  modalUntitled: "Untitled protocol",
  descriptionToggleHint: "Toggle full description",
};

// 用于取消重复请求的 AbortController
let abortController = null;

const createEmptyFilterGroup = () => ({ popular: [], others: [] });

function normalizeFilterGroup(group, fallbackLimit = 10) {
  if (!group) {
    return createEmptyFilterGroup();
  }

  if (Array.isArray(group)) {
    return {
      popular: group.slice(0, fallbackLimit),
      others: group.slice(fallbackLimit),
    };
  }

  const popular = Array.isArray(group.popular) ? group.popular : [];
  let others = Array.isArray(group.others) ? group.others : [];
  const allValues = Array.isArray(group.all) ? group.all : [];

  if (!popular.length && allValues.length) {
    return {
      popular: allValues.slice(0, fallbackLimit),
      others: allValues.slice(fallbackLimit),
    };
  }

  if (!others.length && allValues.length) {
    others = allValues.filter((value) => !popular.includes(value));
  }

  return {
    popular,
    others,
  };
}

function normalizeFiltersPayload(payload) {
  return {
    categories: normalizeFilterGroup(payload?.categories),
    blockchains: normalizeFilterGroup(payload?.blockchains),
  };
}

function truncateText(text, maxLength = 100) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

// 生成报告 URL
function getReportUrl(protocol) {
  if (!protocol) return '#';
  const slug = protocol.slug || (protocol.name
    ? protocol.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : '');
  return `/report/${encodeURIComponent(slug)}`;
}

// 生成 Pools URL
function getPoolsUrl(protocol) {
  if (!protocol) return '#';
  const slug = protocol.slug || (protocol.name
    ? protocol.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : '');
  return `/pools/${encodeURIComponent(slug)}`;
}

// Calculate grade from score (same logic as backend)
function calculateGrade(score) {
  if (score === null || score === undefined || typeof score !== 'number') {
    return 'N/A';
  }
  
  // Litepaper 标准：5 级评分系统
  if (score >= 90) return 'AAA';
  if (score >= 75) return 'AA-A';
  if (score >= 60) return 'BBB-B';
  if (score >= 40) return 'CCC';
  return 'D';
}

// Format timestamp to relative time in English (e.g., "2 hours ago", "3 days ago")
function formatTimeAgo(timestamp) {
  if (!timestamp) return "Unknown";
  
  try {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid timestamp:", timestamp);
      return "Unknown";
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    // Check if the date is in the future (shouldn't happen, but handle it)
    if (diffInSeconds < 0) {
      return "just now";
    }
    
    if (diffInSeconds < 60) {
      return "just now";
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
  } catch (error) {
    console.error("Error formatting time ago:", error, "timestamp:", timestamp);
    return "Unknown";
  }
}

// Main App Component (Homepage)
function MainApp() {
  const [filter, setFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBlockchain, setSelectedBlockchain] = useState("");
  const [selectedToken, setSelectedToken] = useState("");
  // 默认按信用分排序，综合排序去掉
  const [sortBy, setSortBy] = useState("score");
  const [categorySearch, setCategorySearch] = useState("");
  const [blockchainSearch, setBlockchainSearch] = useState("");
  const [tokenSearch, setTokenSearch] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showBlockchainDropdown, setShowBlockchainDropdown] = useState(false);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);
  const blockchainDropdownRef = useRef(null);
  const tokenDropdownRef = useRef(null);
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'card' // 默认按分数排序
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [filters, setFilters] = useState(() => normalizeFiltersPayload());
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const tokenSearchTimerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryHasMore, setCategoryHasMore] = useState(true);
  const [categoryLoadingMore, setCategoryLoadingMore] = useState(false);
  const [blockchainPage, setBlockchainPage] = useState(1);
  const [blockchainHasMore, setBlockchainHasMore] = useState(true);
  const [blockchainLoadingMore, setBlockchainLoadingMore] = useState(false);
  const [tokenPage, setTokenPage] = useState(1);
  const [tokenHasMore, setTokenHasMore] = useState(true);
  const [tokenLoadingMore, setTokenLoadingMore] = useState(false);

  const loadProjects = useCallback(async (page = 1, newSearch = false, signal = null) => {
    try {
      if (page === 1) {
        setLoading(true);
      }
      const data = await fetchDefiProtocols(page, filter, selectedCategory, selectedBlockchain, selectedToken, sortBy, signal);
      // 检查是否请求被取消
      if (signal?.aborted) return;
      
      if (newSearch || page === 1) {
        setProjects(data.protocols);
      } else {
        setProjects(prev => [...prev, ...data.protocols]);
      }
      setPagination(data.pagination);
      // 如果还没有通过 /api/options 预加载选项，才用接口返回的 filters 作为兜底
      if (!optionsLoaded) {
        setFilters(normalizeFiltersPayload(data.filters));
      }
      setCurrentPage(page);
    } catch (err) {
      // 忽略取消请求的错误
      if (err.name === 'AbortError') return;
      setError(t.loadError);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, selectedCategory, selectedBlockchain, selectedToken, sortBy, optionsLoaded]);

  // 懒加载选项：在第一次打开任意一个下拉时才调用 options 接口（带分页）
  const loadOptionsIfNeeded = useCallback(async () => {
    if (optionsLoaded) return;
    try {
      const PAGE_SIZE = 10;
      const [catRes, chainRes, tokenRes] = await Promise.all([
        fetchCategoryOptions(1, PAGE_SIZE),
        fetchBlockchainOptions(1, PAGE_SIZE),
        fetchTokenOptions(1, PAGE_SIZE),
      ]);

      const categoriesArr = (catRes.items || []).map(i => i.value).filter(Boolean);
      const blockchainsArr = (chainRes.items || []).map(i => i.value).filter(Boolean);

      const normalizedFilters = normalizeFiltersPayload({
        categories: {
          popular: categoriesArr,
          others: [],
          all: categoriesArr,
        },
        blockchains: {
          popular: blockchainsArr,
          others: [],
          all: blockchainsArr,
        },
      });
      setFilters(normalizedFilters);

      if (Array.isArray(tokenRes.items)) {
        setTokens(tokenRes.items.map(i => i.value).filter(Boolean));
      }

      setCategoryPage(1);
      setCategoryHasMore(!!catRes.hasMore);
      setBlockchainPage(1);
      setBlockchainHasMore(!!chainRes.hasMore);
      setTokenPage(1);
      setTokenHasMore(!!tokenRes.hasMore);
      setOptionsLoaded(true);
    } catch (e) {
      // 静默失败，不影响主数据加载
      console.error("Failed to load options:", e);
    }
  }, [optionsLoaded]);

  // 下拉滚动加载更多分类
  const handleCategoryScroll = async (event) => {
    if (!optionsLoaded || categoryLoadingMore || !categoryHasMore || categorySearch.trim()) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 40) return;

    try {
      setCategoryLoadingMore(true);
      const nextPage = categoryPage + 1;
      const res = await fetchCategoryOptions(nextPage, 10);
      const newValues = (res.items || []).map(i => i.value).filter(Boolean);
      if (newValues.length) {
        setFilters(prev => {
          const prevCats = [
            ...(prev.categories?.popular || []),
            ...(prev.categories?.others || []),
          ];
          const merged = Array.from(new Set([...prevCats, ...newValues]));
          return {
            ...prev,
            categories: {
              popular: merged,
              others: [],
              all: merged,
            },
          };
        });
      }
      setCategoryPage(nextPage);
      setCategoryHasMore(!!res.hasMore);
    } catch (e) {
      console.error('Failed to load more categories:', e);
    } finally {
      setCategoryLoadingMore(false);
    }
  };

  // 下拉滚动加载更多链
  const handleBlockchainScroll = async (event) => {
    if (!optionsLoaded || blockchainLoadingMore || !blockchainHasMore || blockchainSearch.trim()) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 40) return;

    try {
      setBlockchainLoadingMore(true);
      const nextPage = blockchainPage + 1;
      const res = await fetchBlockchainOptions(nextPage, 10);
      const newValues = (res.items || []).map(i => i.value).filter(Boolean);
      if (newValues.length) {
        setFilters(prev => {
          const prevChains = [
            ...(prev.blockchains?.popular || []),
            ...(prev.blockchains?.others || []),
          ];
          const merged = Array.from(new Set([...prevChains, ...newValues]));
          return {
            ...prev,
            blockchains: {
              popular: merged,
              others: [],
              all: merged,
            },
          };
        });
      }
      setBlockchainPage(nextPage);
      setBlockchainHasMore(!!res.hasMore);
    } catch (e) {
      console.error('Failed to load more blockchains:', e);
    } finally {
      setBlockchainLoadingMore(false);
    }
  };

  // 下拉滚动加载更多 tokens（仅在无搜索关键字时使用热门 token）
  const handleTokenScroll = async (event) => {
    if (!optionsLoaded || tokenLoadingMore || !tokenHasMore || tokenSearch.trim()) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 40) return;

    try {
      setTokenLoadingMore(true);
      const nextPage = tokenPage + 1;
      const res = await fetchTokenOptions(nextPage, 10);
      const newValues = (res.items || []).map(i => i.value).filter(Boolean);
      if (newValues.length) {
        setTokens(prev => Array.from(new Set([...prev, ...newValues])));
      }
      setTokenPage(nextPage);
      setTokenHasMore(!!res.hasMore);
    } catch (e) {
      console.error('Failed to load more tokens:', e);
    } finally {
      setTokenLoadingMore(false);
    }
  };

  // Track initial load
  const isInitialMount = useRef(true);
  
  // Initial load and handle filter changes with debouncing
  useEffect(() => {
    // 取消之前的请求
    if (abortController) {
      abortController.abort();
    }
    
    // 创建新的 AbortController
    abortController = new AbortController();
    const signal = abortController.signal;
    
    // 首次加载立即执行
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadProjects(1, true, signal);
      return () => {
        if (abortController) {
          abortController.abort();
          abortController = null;
        }
      };
    }

    // 后续变化使用防抖
    const timer = setTimeout(() => {
      if (!signal.aborted) {
        loadProjects(1, true, signal);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedCategory, selectedBlockchain, selectedToken, sortBy]);

  const loadMoreProjects = useCallback(async () => {
    if (loadingMore || !pagination?.hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      await loadProjects(nextPage);
    } catch (err) {
      setError(t.loadMoreError);
      setLoadingMore(false);
    }
  }, [loadingMore, pagination?.hasMore, currentPage, loadProjects]);

  useEffect(() => {
    if (!pagination?.hasMore || loadingMore) return;

    let observer = null;
    let currentRef = null;

    // 使用 requestAnimationFrame 确保 DOM 已经渲染
    const rafId = requestAnimationFrame(() => {
      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && !loadingMore && pagination?.hasMore) {
            loadMoreProjects();
          }
        },
        {
          root: null,
          rootMargin: "300px",
          threshold: 0.1,
        }
      );

      currentRef = loadMoreRef.current;
      if (currentRef) {
        observer.observe(currentRef);
        
        // 立即检查一次，如果元素已经在视口中，立即加载
        const rect = currentRef.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight + 300;
        if (isVisible && !loadingMore && pagination?.hasMore) {
          loadMoreProjects();
        }
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (observer && currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [pagination?.hasMore, loadingMore, loadMoreProjects, projects.length]);

  const handleScoreClick = async (project) => {
    if (!project || typeof project.score !== "number") return;

    // 跟踪报告查看
    trackReportView(project.name, project.id);

    setSelectedProject(project);
    setIsReportModalOpen(true);
    setReportLoading(true);
    setReportError(null);
    setSelectedReport(null);

    try {
      const report = await fetchProtocolScore(project.id);
      setSelectedReport(report);
    } catch (err) {
      setReportError(t.reportError);
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportModal = () => {
    setIsReportModalOpen(false);
    setSelectedProject(null);
    setSelectedReport(null);
    setReportError(null);
  };

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(filter.toLowerCase())
  );
  
  const toggleDescription = (projectId) => {
    setExpandedProjectId((prev) => (prev === projectId ? null : projectId));
  };

  const categoryPopularOptions = filters.categories?.popular || [];
  const categoryOtherOptions = filters.categories?.others || [];
  const chainPopularOptions = filters.blockchains?.popular || [];
  const chainOtherOptions = filters.blockchains?.others || [];
  
  // 过滤选项（根据搜索关键词）
  const allCategories = [...categoryPopularOptions, ...categoryOtherOptions];
  const filteredCategories = categorySearch
    ? allCategories.filter(cat => cat.toLowerCase().includes(categorySearch.toLowerCase()))
    : allCategories;
  
  const allBlockchains = [...chainPopularOptions, ...chainOtherOptions];
  const filteredBlockchains = blockchainSearch
    ? allBlockchains.filter(chain => chain.toLowerCase().includes(blockchainSearch.toLowerCase()))
    : allBlockchains;
  
  // 加载 token 列表（仅在搜索时调用 API；如果没有搜索词且已预加载 options，就直接用预加载结果）
  useEffect(() => {
    // 如果下拉菜单关闭，不动 tokens（保留上一次的结果，避免闪烁）
    if (!showTokenDropdown) {
      return;
    }
    
    // 如果没有搜索词：
    // - 若已经通过 /api/options 预加载 tokens，则直接使用预加载结果（不触发搜索）
    // - 若尚未预加载，则清空，等待用户输入后再搜索
    if (!tokenSearch.trim()) {
      if (!optionsLoaded) {
        setTokens([]);
      }
      return;
    }
    
    // 清除之前的定时器
    if (tokenSearchTimerRef.current) {
      clearTimeout(tokenSearchTimerRef.current);
    }
    
    // 防抖：延迟 300ms 后调用 API
    tokenSearchTimerRef.current = setTimeout(() => {
      setTokensLoading(true);
      // 调用 API 搜索 token
      fetchTokens(tokenSearch, 50, 'count')
        .then(data => {
          if (data.success && Array.isArray(data.tokens)) {
            // 提取 token symbol 列表
            const tokenSymbols = data.tokens.map(t => t.symbol || t);
            setTokens(tokenSymbols);
          } else {
            setTokens([]);
          }
        })
        .catch(error => {
          console.error('Failed to load tokens:', error);
          setTokens([]);
        })
        .finally(() => {
          setTokensLoading(false);
        });
    }, 300);
    
    return () => {
      if (tokenSearchTimerRef.current) {
        clearTimeout(tokenSearchTimerRef.current);
      }
    };
  }, [tokenSearch, showTokenDropdown, optionsLoaded]);
  
  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (blockchainDropdownRef.current && !blockchainDropdownRef.current.contains(event.target)) {
        setShowBlockchainDropdown(false);
      }
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target)) {
        setShowTokenDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header filter={filter} setFilter={setFilter} />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-gray-600">{t.loading}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header filter={filter} setFilter={setFilter} />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header filter={filter} setFilter={setFilter} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section - 新定位 */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Find the Safest DeFi Opportunities
          </h1>
          <p className="text-base md:text-lg text-gray-600 mx-auto">
            AI-powered risk assessment to help you avoid pitfalls and discover trusted protocols
          </p>
        </div>
        
        {/* Filters */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 items-stretch">
            {/* Category 可搜索下拉 */}
            <div className="flex-1 min-w-[180px] relative" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  loadOptionsIfNeeded();
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowBlockchainDropdown(false);
                }}
                className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
              >
                <span>{selectedCategory || t.allCategories}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-64" onScroll={handleCategoryScroll}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("");
                        setShowCategoryDropdown(false);
                        setCategorySearch("");
                        trackFilter('category', 'all');
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedCategory ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {t.allCategories}
                    </button>
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(cat);
                            trackFilter('category', cat);
                            setShowCategoryDropdown(false);
                            setCategorySearch("");
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCategory === cat ? 'bg-blue-50 text-blue-600' : ''}`}
                        >
                          {cat}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">No categories found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Blockchain 可搜索下拉 */}
            <div className="flex-1 min-w-[180px] relative" ref={blockchainDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  loadOptionsIfNeeded();
                  setShowBlockchainDropdown(!showBlockchainDropdown);
                  setShowCategoryDropdown(false);
                }}
                className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
              >
                <span>{selectedBlockchain || t.allChains}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${showBlockchainDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showBlockchainDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search blockchains..."
                      value={blockchainSearch}
                      onChange={(e) => setBlockchainSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-64" onScroll={handleBlockchainScroll}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBlockchain("");
                        trackFilter('blockchain', 'all');
                        setShowBlockchainDropdown(false);
                        setBlockchainSearch("");
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedBlockchain ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {t.allChains}
                    </button>
                    {filteredBlockchains.length > 0 ? (
                      filteredBlockchains.map((chain) => (
                        <button
                          key={chain}
                          type="button"
                          onClick={() => {
                            setSelectedBlockchain(chain);
                            trackFilter('blockchain', chain);
                            setShowBlockchainDropdown(false);
                            setBlockchainSearch("");
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedBlockchain === chain ? 'bg-blue-50 text-blue-600' : ''}`}
                        >
                          {chain}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">No blockchains found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Token 可搜索下拉 */}
            <div className="flex-1 min-w-[180px] relative" ref={tokenDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  loadOptionsIfNeeded();
                  setShowTokenDropdown(!showTokenDropdown);
                  setShowCategoryDropdown(false);
                  setShowBlockchainDropdown(false);
                }}
                className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
              >
                <span>{selectedToken || "All Tokens"}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${showTokenDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showTokenDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search tokens..."
                      value={tokenSearch}
                      onChange={(e) => {
                        setTokenSearch(e.target.value);
                        // 搜索时清空列表，useEffect 会触发重新加载（带防抖）
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-64" onScroll={handleTokenScroll}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedToken("");
                        trackFilter('token', 'all');
                        setShowTokenDropdown(false);
                        setTokenSearch("");
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedToken ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      All Tokens
                    </button>
                    {tokensLoading ? (
                      <div className="px-4 py-2 text-sm text-gray-500">Loading tokens...</div>
                    ) : tokens.length > 0 ? (
                      tokens.map((token) => {
                        const tokenSymbol = typeof token === 'string' ? token : (token.symbol || '');
                        return (
                        <button
                            key={tokenSymbol}
                          type="button"
                          onClick={() => {
                              setSelectedToken(tokenSymbol);
                              trackFilter('token', tokenSymbol);
                            setShowTokenDropdown(false);
                            setTokenSearch("");
                          }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedToken === tokenSymbol ? 'bg-blue-50 text-blue-600' : ''}`}
                        >
                            {tokenSymbol}
                        </button>
                        );
                      })
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">No tokens found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <select
              id="sort-filter"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="flex-1 min-w-[180px] h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.625rem center',
                backgroundSize: '12px 12px'
              }}
            >
              <option value="score">{t.sortByScore}</option>
              <option value="tvl">{t.sortByTvl}</option>
            </select>
            
            {/* View Mode Toggle - Icon Buttons */}
            <div className="flex items-center gap-1 border border-gray-300 rounded-lg bg-white h-10">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`h-full aspect-square flex items-center justify-center rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-label="List View"
                title="List View"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`h-full aspect-square flex items-center justify-center rounded transition-colors ${
                  viewMode === 'card'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-label="Card View"
                title="Card View"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <section 
          className={viewMode === 'card' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
            : "space-y-3"
          } 
          aria-label="DeFi Protocols"
        >
          {/* Table Header for List Mode - Desktop Only */}
          {viewMode === 'list' && filteredProjects.length > 0 && (
            <div className="hidden md:block bg-gray-50 rounded-lg border border-gray-200 px-3.5 py-3">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12"></div>
                <div className="flex-shrink-0 min-w-[200px]">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Protocol</div>
                </div>
                <div className="flex-shrink-0 min-w-[75px] flex justify-center mr-6">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Score</div>
                </div>
                <div className="flex-shrink-0 min-w-[130px]">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Chain</div>
                </div>
                <div className="flex-shrink-0 min-w-[110px] text-right">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">TVL</div>
                </div>
                <div className="flex-1 min-w-0 px-3">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</div>
                </div>
                <div className="flex-shrink-0">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide"></div>
                </div>
              </div>
            </div>
          )}
          
          {filteredProjects.map((project) => {
            const hasScore = typeof project.score === "number";
            const scoreValue = hasScore
              ? Math.round(project.score)
              : t.noScoreText;
            const scoreBadgeColor = hasScore
              ? scoreValue >= 90
                ? "bg-green-100 text-green-800"
                : scoreValue >= 75
                ? "bg-yellow-100 text-yellow-800"
                : "bg-rose-100 text-rose-800"
              : "bg-gray-100 text-gray-500";
            const homepageUrl = project.url && project.url !== "#" ? project.url : null;
            const appUrl = project.app_url || null;
            const summaryRaw = project.description?.trim();
            const summary = summaryRaw && summaryRaw.length > 0 ? summaryRaw : t.noDescription;
            const shouldTruncate = summary.length > 100;
            const isExpanded = expandedProjectId === project.id;
            const displaySummary =
              isExpanded || !shouldTruncate ? summary : truncateText(summary, 100);
            return (
              <article
                key={project.id}
                  className={viewMode === 'card'
                  ? "relative bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md hover:border-gray-200 transition-all duration-200"
                  : "relative bg-white rounded-lg border border-gray-200 p-3.5 hover:border-blue-300 hover:shadow-sm transition-all duration-200"
                }
              >
                {viewMode === 'card' ? (
                  <>
                    {/* Card Mode Layout */}
                    {/* Score Badge */}
                    <div className="absolute top-5 right-5 z-10">
                      {hasScore ? (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleScoreClick(project)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleScoreClick(project);
                          }}
                          className={`inline-flex flex-col items-center cursor-pointer rounded-lg px-3 py-2 shadow-sm hover:shadow-md transition-shadow ${scoreBadgeColor}`}
                          aria-label={t.openReport(project.name)}
                        >
                          <div className="text-2xl font-bold leading-none">
                            {scoreValue}
                          </div>
                          {project.lastScoreUpdatedAt && (
                            <div className="text-xs opacity-75 mt-1">
                              {formatTimeAgo(project.lastScoreUpdatedAt)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="inline-flex flex-col items-center rounded-lg px-3 py-2 bg-gray-50 border border-gray-200">
                          <div className="text-xs text-gray-500">{scoreValue}</div>
                        </div>
                      )}
                    </div>

                    {/* Header Section */}
                    <div className="flex items-start space-x-4 pr-28 mb-5">
                      {project.logo ? (
                        <img
                          src={project.logo}
                          alt={`${project.name} logo`}
                          className="w-14 h-14 rounded-xl flex-shrink-0 border border-gray-100 object-cover bg-gray-50"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56'%3E%3Crect width='56' height='56' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='20' fill='%239ca3af'%3E" + (project.name?.charAt(0)?.toUpperCase() || '?') + "%3C/text%3E%3C/svg%3E";
                          }}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                          <span className="text-xl font-bold text-gray-400">
                            {project.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h2 
                          className={`text-xl font-bold text-gray-900 mb-1 truncate ${hasScore ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
                          onClick={() => hasScore && handleScoreClick(project)}
                          role={hasScore ? "button" : undefined}
                          tabIndex={hasScore ? 0 : undefined}
                          onKeyDown={(e) => {
                            if (hasScore && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              handleScoreClick(project);
                            }
                          }}
                          aria-label={hasScore ? t.openReport(project.name) : undefined}
                        >
                          {project.name}
                        </h2>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {project.category}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {project.blockchain}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats Section */}
                    <div className="mb-5 pb-5 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            {t.tvlLabel}
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            ${(project.tvl / 1e6).toFixed(2)}M
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description Section */}
                    {summary && (
                      <div className="mb-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          {t.summaryLabel}
                        </p>
                        <button
                          type="button"
                          onClick={() => toggleDescription(project.id)}
                          className="w-full text-left text-sm text-gray-700 leading-relaxed"
                          aria-label={t.descriptionToggleHint}
                        >
                          {displaySummary}
                          {shouldTruncate && (
                            <span className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-700">
                              {isExpanded ? t.collapse : t.readMore}
                            </span>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-3">
                      <a
                        href={homepageUrl || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex-1 rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors ${
                          homepageUrl
                            ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        aria-disabled={!homepageUrl}
                      >
                        {homepageUrl ? t.website : t.homepageUnavailable}
                      </a>
                      <a
                        href={getPoolsUrl(project)}
                        className="flex-1 rounded-lg px-4 py-2.5 text-center text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow transition-colors"
                      >
                        View Pools
                      </a>
                      {hasScore && (
                        <a
                          href={getReportUrl(project)}
                          className="flex-1 rounded-lg px-4 py-2.5 text-center text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow transition-colors"
                        >
                          {t.viewReport}
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* List Mode Layout - 平铺展示 */}
                    {/* Mobile Layout */}
                    <div className="md:hidden space-y-3">
                      {/* Top Row: Logo, Name, Score */}
                      <div className="flex items-start gap-3">
                        {/* Logo */}
                        <div className="flex-shrink-0">
                          {project.logo ? (
                            <img
                              src={project.logo}
                              alt={`${project.name} logo`}
                              className="w-10 h-10 rounded-lg border border-gray-200 object-cover bg-gray-50"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%239ca3af'%3E" + (project.name?.charAt(0)?.toUpperCase() || '?') + "%3C/text%3E%3C/svg%3E";
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
                              <span className="text-base font-bold text-gray-400">
                                {project.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Name & Score */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <h2 
                              className={`text-sm font-bold text-gray-900 truncate ${hasScore ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
                              onClick={() => hasScore && handleScoreClick(project)}
                              role={hasScore ? "button" : undefined}
                              tabIndex={hasScore ? 0 : undefined}
                              onKeyDown={(e) => {
                                if (hasScore && (e.key === "Enter" || e.key === " ")) {
                                  e.preventDefault();
                                  handleScoreClick(project);
                                }
                              }}
                              aria-label={hasScore ? t.openReport(project.name) : undefined}
                            >
                              {project.name}
                            </h2>
                            {/* Score */}
                            {hasScore ? (
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => handleScoreClick(project)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleScoreClick(project);
                                }}
                                className={`inline-flex flex-col items-center justify-center cursor-pointer rounded-lg px-2.5 py-1 shadow-sm ${scoreBadgeColor}`}
                                aria-label={t.openReport(project.name)}
                              >
                                <div className="text-base font-bold leading-none">
                                  {scoreValue}
                                </div>
                                {project.lastScoreUpdatedAt && (
                                  <div className="text-[10px] opacity-75 mt-0.5">
                                    {formatTimeAgo(project.lastScoreUpdatedAt)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="inline-flex items-center justify-center rounded-lg px-2.5 py-1 bg-gray-50 border border-gray-200">
                                <div className="text-xs text-gray-500">{scoreValue}</div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                              {project.category}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                              {project.blockchain}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* TVL & Action */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-bold text-gray-900">
                          ${(project.tvl / 1e6).toFixed(2)}M
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={homepageUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-block px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                              homepageUrl
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "bg-gray-200 text-gray-500 cursor-not-allowed"
                            }`}
                          >
                            {homepageUrl ? t.website : t.homepageUnavailable}
                          </a>
                          <a
                            href={getPoolsUrl(project)}
                            className="inline-block px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all whitespace-nowrap"
                          >
                            Pools
                          </a>
                          {hasScore && (
                            <a
                              href={getReportUrl(project)}
                              className="inline-block px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all whitespace-nowrap"
                            >
                              {t.viewReport}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleDescription(project.id)}
                          className="w-full text-left text-xs text-gray-600 leading-relaxed line-clamp-2 hover:text-gray-900 transition-colors"
                          aria-label={t.descriptionToggleHint}
                        >
                          {displaySummary}
                          {shouldTruncate && (
                            <span className="ml-1.5 text-xs font-medium text-blue-600 hover:text-blue-700">
                              {isExpanded ? t.collapse : t.readMore}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:flex items-center gap-3">
                      {/* Logo */}
                      <div className="flex-shrink-0">
                        {project.logo ? (
                          <img
                            src={project.logo}
                            alt={`${project.name} logo`}
                            className="w-12 h-12 rounded-lg border border-gray-200 object-cover bg-gray-50"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect width='48' height='48' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='%239ca3af'%3E" + (project.name?.charAt(0)?.toUpperCase() || '?') + "%3C/text%3E%3C/svg%3E";
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
                            <span className="text-lg font-bold text-gray-400">
                              {project.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Name & Category */}
                      <div className="flex-shrink-0 min-w-[200px]">
                        <h2 
                          className={`text-base font-bold text-gray-900 truncate mb-1.5 ${hasScore ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
                          onClick={() => hasScore && handleScoreClick(project)}
                          role={hasScore ? "button" : undefined}
                          tabIndex={hasScore ? 0 : undefined}
                          onKeyDown={(e) => {
                            if (hasScore && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              handleScoreClick(project);
                            }
                          }}
                          aria-label={hasScore ? t.openReport(project.name) : undefined}
                        >
                          {project.name}
                        </h2>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {project.category}
                          </span>
                        </div>
                      </div>

                      {/* Score */}
                      <div className="flex-shrink-0 min-w-[75px] flex justify-center mr-6">
                        {hasScore ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => handleScoreClick(project)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleScoreClick(project);
                            }}
                            className={`inline-flex items-center justify-center cursor-pointer rounded-lg px-3 py-1.5 shadow-sm hover:shadow transition-shadow ${scoreBadgeColor}`}
                            aria-label={t.openReport(project.name)}
                          >
                            <div className="text-lg font-bold leading-none">
                              {scoreValue}
                            </div>
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 bg-gray-50 border border-gray-200">
                            <div className="text-xs text-gray-500">{scoreValue}</div>
                          </div>
                        )}
                      </div>

                      {/* Blockchain */}
                      <div className="flex-shrink-0 min-w-[130px]">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                          {project.blockchain}
                        </span>
                      </div>

                      {/* TVL */}
                      <div className="flex-shrink-0 min-w-[110px] text-right">
                        <div className="text-sm font-bold text-gray-900">
                          ${(project.tvl / 1e6).toFixed(2)}M
                        </div>
                      </div>

                      {/* Description */}
                      <div className="flex-1 min-w-0 px-3">
                        <button
                          type="button"
                          onClick={() => toggleDescription(project.id)}
                          className="w-full text-left text-xs text-gray-600 leading-relaxed line-clamp-2 hover:text-gray-900 transition-colors"
                          aria-label={t.descriptionToggleHint}
                        >
                          {displaySummary}
                          {shouldTruncate && (
                            <span className="ml-1.5 text-xs font-medium text-blue-600 hover:text-blue-700">
                              {isExpanded ? t.collapse : t.readMore}
                            </span>
                          )}
                        </button>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex-shrink-0 flex gap-2">
                        <a
                          href={homepageUrl || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-block px-4 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                            homepageUrl
                              ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          }`}
                          aria-disabled={!homepageUrl}
                        >
                          {homepageUrl ? t.website : t.homepageUnavailable}
                        </a>
                        <a
                          href={getPoolsUrl(project)}
                          className="inline-block px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md transition-all whitespace-nowrap"
                        >
                          Pools
                        </a>
                        {hasScore && (
                          <a
                            href={getReportUrl(project)}
                            className="inline-block px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md transition-all whitespace-nowrap"
                          >
                            {t.viewReport}
                          </a>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </section>
        
        {pagination?.hasMore && (
          <div className="mt-8 text-center">
            <div ref={loadMoreRef} className="h-10"></div>
            <button
              onClick={loadMoreProjects}
              disabled={loadingMore}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              aria-label={loadingMore ? t.loadingMoreText : t.loadMoreButton}
            >
              {loadingMore ? t.loadingMoreText : t.loadMoreButton}
            </button>
          </div>
        )}
        
        {pagination && (
          <div className="mt-4 text-center text-sm text-gray-600" role="status" aria-live="polite">
            {t.paginationStatus(filteredProjects.length, pagination.totalItems)}
          </div>
        )}
      </main>

      {isReportModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeReportModal}
        >
          <div 
            className="w-full max-w-3xl rounded-lg bg-white shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div>
                <p className="text-sm uppercase tracking-wide text-gray-400">{t.modalTitle}</p>
                <h3 className="text-2xl font-semibold text-gray-900">
                  {selectedProject?.name || t.modalUntitled}
                </h3>
                <p className="text-sm text-gray-500">{selectedProject?.category}</p>
              </div>
              <button
                type="button"
                onClick={closeReportModal}
                className="text-gray-400 hover:text-gray-600 text-3xl md:text-2xl w-10 h-10 md:w-8 md:h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                aria-label={t.modalClose}
              >
                ✕
              </button>
            </div>

            <div className="mt-6 p-6 overflow-y-auto flex-1">
              {reportLoading && (
                <div className="text-center text-gray-500">{t.reportLoading}</div>
              )}

              {reportError && (
                <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
                  {reportError}
                </div>
              )}

              {!reportLoading && !reportError && selectedReport && selectedProject && (
                <div className="space-y-6">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                    <div className="mt-2">
                      <div className="text-4xl font-bold text-gray-900">
                      {/* 使用与首页相同的分数源 (selectedProject.score) */}
                      {typeof selectedProject.score === 'number' ? selectedProject.score : (selectedReport?.overall_risk?.risk_score ?? "—")}
                      <span className="ml-3 text-lg font-semibold text-gray-500">
                        {/* 使用与首页相同的分数计算评级 */}
                        {typeof selectedProject.score === 'number' 
                          ? calculateGrade(selectedProject.score) 
                          : (selectedReport?.overall_risk?.grade ?? "—")}
                      </span>
                    </div>
                      {(selectedProject.lastScoreUpdatedAt || selectedReport?.timestamp) && (
                        <div className="text-xs text-gray-500 mt-1">
                          Updated {formatTimeAgo(selectedProject.lastScoreUpdatedAt || selectedReport?.timestamp)}
                        </div>
                      )}
                    </div>
                      {typeof selectedProject.score === 'number' && (
                        <GradeLegend grade={calculateGrade(selectedProject.score)} compact={true} />
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                      {selectedReport?.overall_risk?.risk_summary_text}
                    </p>
                  </div>

                  {selectedReport?.top_risk_drivers?.length > 0 && (
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">{t.coreRisks}</h4>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
                        {selectedReport.top_risk_drivers.map((item, index) => (
                          <li key={`${selectedReport.protocol_id}-risk-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedReport?.investment_suggestion?.actionable_advice?.length > 0 && (
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">{t.actionableAdvice}</h4>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
                        {selectedReport.investment_suggestion.actionable_advice.map(
                          (advice, index) => (
                            <li key={`${selectedReport.protocol_id}-advice-${index}`}>
                              {advice}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  {selectedReport?.disclaimer?.length > 0 && (
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">{t.disclaimers}</h4>
                      <div className="mt-2 rounded-md bg-gray-50 p-4 text-xs text-gray-500">
                        {selectedReport.disclaimer.map((item, index) => (
                          <p key={`${selectedReport.protocol_id}-disclaimer-${index}`} className="mt-1">
                            {item}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Router Component
function App() {
  const pathname = window.location.pathname;
  
  // Check if we're on admin page
  const isAdminPage = pathname === '/admin' || pathname === '/admin/';
  if (isAdminPage) {
    return <Admin />;
  }
  
  // Check if we're on report page: /report/{slug}
  const reportMatch = pathname.match(/^\/report\/(.+)$/);
  if (reportMatch) {
    const slug = decodeURIComponent(reportMatch[1]);
    return <Report slug={slug} />;
  }
  
  // Check if we're on pools compare page: /pools-compare
  const isPoolsComparePage = pathname === '/pools-compare' || pathname === '/pools-compare/';
  if (isPoolsComparePage) {
    return <PoolsCompare />;
  }
  
  // Check if we're on pools page: /pools/{slug}
  const poolsMatch = pathname.match(/^\/pools\/(.+)$/);
  if (poolsMatch) {
    const slug = decodeURIComponent(poolsMatch[1]);
    return <Pools slug={slug} />;
  }
  
  // Check if we're on AI Agent analysis page
  const isAIAgentPage = pathname === '/ai-agent' || pathname === '/ai-agent/';
  if (isAIAgentPage) {
    return <AIAgent />;
  }
  
  return <MainApp />;
}

export default App;